import { formatNumber, formatDate } from '@niaratech/niara-js-commons';
import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { FieldValidationHandler } from '../types';
import useFormContext from './useFormContext';
import shallowEqual from 'fbjs/lib/shallowEqual';
import _uniq from 'lodash/uniq';
import { makeInvalidRegExpValidator, combineValidators } from '../../validators';
import { useImmutableFunction } from '@niaratech/niara-react-components';
export type UseFormInputReturn<F> = {
  setValue: React.Dispatch<React.SetStateAction<F>>;
  getValue: () => F;
  value: F;
  errors?: string[];
  disabled?: boolean;
  ref: React.MutableRefObject<HTMLElement>;
};
type FieldAlternatives<F> = {
  fieldPath: string;
} | {
  fieldMapping: Record<keyof F, string>;
};
export type UseFormInputParams<F> = {
  placeholderValue?: F;
  validator?: FieldValidationHandler<any, F>;
  required?: boolean;
  min?: F;
  max?: F;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  invalidPattern?: string;
  autoValidation?: boolean | 'FORCE';
  disabled?: boolean;
} & FieldAlternatives<F>;
export default function useFormInput<F>(params: UseFormInputParams<F>): UseFormInputReturn<F> {
  const {
    validator: propValidator,
    required,
    min,
    max,
    minLength,
    maxLength,
    pattern,
    invalidPattern,
    autoValidation: autoValidationFormInput,
    disabled: disabledFormInput
  } = params;
  const fieldPath: string | never = 'fieldPath' in params && params.fieldPath;

  // lógica pra não mudar a instância de fieldMapping se a nova instância é shallowEqual
  const _propfieldMapping = 'fieldMapping' in params && params.fieldMapping;
  const _fieldMappingRef = useRef<Record<string, string>>(_propfieldMapping);
  const fieldMapping: Record<string, string> | never = _fieldMappingRef.current;
  useEffect(() => {
    //update fieldMappingRef if changed
    if (!shallowEqual(_fieldMappingRef.current, _propfieldMapping)) {
      _fieldMappingRef.current = _propfieldMapping;
    }
  });
  const {
    addRemoveFieldValidator,
    fieldErrors,
    getField,
    setField,
    disabled: disabledForm,
    value: entityValue,
    addHtmlElementRef,
    removeHtmlElementRef,
    autoValidation: autoValidationForm
  } = useFormContext();
  const autoValidation = autoValidationFormInput ?? autoValidationForm;
  const getValue = useImmutableFunction(() => {
    if (fieldPath) {
      return getField(fieldPath);
    } else if (fieldMapping) {
      return Object.keys(fieldMapping).reduce((acc, key) => {
        const fieldValue = getField(fieldMapping[key]);
        if (fieldValue != null) {
          acc = acc || {} as F;
          acc[key] = getField(fieldMapping[key]);
        }
        return acc;
      }, undefined as F);
    } else {
      return null;
    }
  });
  const valueRef = useRef<F>(getValue());
  const valueChangedAtLeastOnceRef = useRef<boolean>(false);
  const _value = getValue();
  valueChangedAtLeastOnceRef.current = valueChangedAtLeastOnceRef.current || _value !== valueRef.current;
  valueRef.current = _value;
  const setValue = useImmutableFunction<React.Dispatch<React.SetStateAction<F>>>((action: React.SetStateAction<F>) => {
    if (disabledFormInput || disabledForm) {
      return;
    }
    const newValue = typeof action == 'function' ? (action as (p: F) => F)(valueRef.current) : action; // assume que F não é function
    valueChangedAtLeastOnceRef.current = valueChangedAtLeastOnceRef.current || newValue !== valueRef.current;
    valueRef.current = newValue;
    if (fieldMapping) {
      for (const key of Object.keys(fieldMapping)) {
        setField(fieldMapping[key], newValue?.[key]);
      }
    } else {
      setField(fieldPath, newValue);
    }
  });
  const errors = useMemo(() => {
    if (fieldPath) {
      return fieldErrors?.[fieldPath] || null;
    } else if (fieldMapping) {
      return _uniq(Object.values(fieldMapping as Record<string, string>).reduce((acc, entry) => {
        const errors = fieldErrors?.[entry];
        if (errors?.length > 0) {
          acc.push(...errors);
        }
        return acc;
      }, []));
    }
  }, [fieldErrors, fieldMapping, fieldPath]);
  const invalidRegExpValidator = useMemo(() => invalidPattern && makeInvalidRegExpValidator(invalidPattern) || null, [invalidPattern]);
  const validator: FieldValidationHandler<any, F> = propValidator && invalidRegExpValidator ? combineValidators(propValidator, invalidRegExpValidator) : propValidator || invalidRegExpValidator;
  const validatorRef = React.useRef(validator);
  validatorRef.current = validator;
  const validations = React.useMemo(() => {
    const validations: Array<FieldValidationHandler<any, F>> = [(field, v) => {
      return validatorRef.current ? validatorRef.current(field, v) : undefined;
    }];
    if (required) {
      validations.push(field => {
        if (field == null || field === '' as unknown as F || Array.isArray(field) && field.length == 0) return {
          errors: ["Campo obrigatório"]
        };
      });
    }
    // validador minLength
    if (minLength != null) {
      validations.push(field => {
        const length = (field as unknown as string)?.length || 0;
        if (length > 0 && length < minLength) {
          // OBS: length == 0 ignorado - combinar minLength com required
          return {
            errors: [`Mínimo ${minLength} caracteres`]
          };
        }
      });
    }
    if (maxLength != null) {
      validations.push(field => {
        if (field != null && (field as unknown as string).length > maxLength) return {
          errors: [`Máximo ${maxLength} caracteres`]
        };
      });
    }
    if (pattern) {
      const regexp = new RegExp(pattern);
      validations.push(field => {
        if (field != null && !regexp.test(field as unknown as string)) {
          return {
            errors: [`Formato inválido`]
          };
        }
      });
    }
    if (max != null) {
      validations.push(field => {
        if (field != null && field as unknown as number > (max as unknown as number)) {
          if (typeof max == 'string' && looksLikeDate.test(max)) {
            return {
              errors: [`Data máxima é ${formatDate(max)}`]
            };
          } else if (typeof max == 'number') {
            return {
              errors: [`Valor máximo é ${formatNumber(max)}`]
            };
          } else {
            return {
              errors: [`Valor máximo é ${max}`]
            };
          }
        }
      });
    }
    if (min != null) {
      validations.push(field => {
        if (field != null && field as unknown as number < (min as unknown as number)) {
          if (typeof min == 'string' && looksLikeDate.test(min)) {
            return {
              errors: [`Data mínima é ${formatDate(min)}`]
            };
          } else if (typeof min == 'number') {
            return {
              errors: [`Valor mínimo é ${formatNumber(min)}`]
            };
          } else {
            return {
              errors: [`Valor mínimo é ${min}`]
            };
          }
        }
      });
    }
    return validations;
  }, [max, maxLength, min, minLength, pattern, required]);

  //registers/ unregisters field validations
  useLayoutEffect(() => {
    if (fieldPath) {
      const fieldValidation = {
        [fieldPath]: validations
      };
      return addRemoveFieldValidator(fieldValidation);
    } else if (fieldMapping) {
      const fieldValidation = Object.values(fieldMapping).reduce((acc, fp) => {
        acc[fp] = validations;
        return acc;
      }, {} as Record<string, typeof validations>);
      return addRemoveFieldValidator(fieldValidation);
    }
  }, [addRemoveFieldValidator, fieldPath, fieldMapping, validations]);

  //validação automática ao trocar o value, não no submit
  const automaticValidationErrors = useMemo(() => {
    if (autoValidation && validations && (valueChangedAtLeastOnceRef.current || autoValidation == 'FORCE')) {
      const errors = validations.reduce<string[]>((acc, validator) => {
        const result = validator(_value, entityValue);
        if (result?.errors?.length > 0) {
          acc.push(...result.errors);
        }
        return acc;
      }, []);
      return errors;
    }
  }, [autoValidation, validations, entityValue, _value]);
  const htmlElementRef = useRef<HTMLElement>();
  // adiciona ao FormContext o inputRef
  useLayoutEffect(() => {
    if (fieldPath) {
      return addHtmlElementRef(fieldPath, htmlElementRef);
    } else if (fieldMapping) {
      const removeFnArray = Object.values(fieldMapping).map(fieldPath => {
        return addHtmlElementRef(fieldPath, htmlElementRef);
      });
      return () => {
        removeFnArray.forEach(fn => fn());
      };
    }
  }, [addHtmlElementRef, removeHtmlElementRef, fieldMapping, fieldPath]);
  const _r: UseFormInputReturn<F> = useMemo(() => {
    return {
      value: _value,
      setValue,
      getValue,
      errors: automaticValidationErrors ?? errors,
      disabled: disabledForm,
      ref: htmlElementRef
    };
  }, [disabledForm, errors, automaticValidationErrors, setValue, getValue, _value]);
  return _r;
}
const looksLikeDate = /^\d{4}-\d{2}-\d{2}$/;